import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { Error } from '@vfmfe/foundations';
import { Background } from './Error.styles';

type ErrorEntry = {
    title?: string;
    description?: string;
    primaryAction?: { label: string; onClick: () => void };
    secondaryAction?: { label: string; onClick: () => void };
};

type ErrorContextType = {
    hasError: boolean;
    setError: (error: ErrorEntry) => void;
    closeError: () => void;
};

const ErrorContext = createContext<ErrorContextType>({
    hasError: false,
    setError: () => {},
    closeError: () => {}
});

const ErrorProvider = ({ children }: { children: ReactNode }) => {
    const [error, setErrorState] = useState<ErrorEntry | null>(null);

    const setError = useCallback((entry: ErrorEntry) => {
        setErrorState(entry);
    }, []);

    const closeError = useCallback(() => {
        setErrorState(null);
    }, []);

    return (
        <ErrorContext.Provider value={{ hasError: !!error, setError, closeError }}>
            {error && (
                <Background>
                    <Error
                        errorType="full"
                        title={error.title}
                        description={error.description}
                        primaryAction={error.primaryAction}
                        secondaryAction={error.secondaryAction}
                    />
                </Background>
            )}
            {children}
        </ErrorContext.Provider>
    );
};

export const useError = () => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};

export default ErrorProvider;
